import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Card, CardHeader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { assistantService, type AssistantKey } from '@/services/assistants';
import { formatDateTime } from '@/utils';

/** Broker settings → AI assistants: every key at this broker, who holds it, its recent calls, and revoke. */

function RecentCalls({ keyId }: { keyId: string }) {
  const calls = useQuery({ queryKey: ['assistant-key-calls', keyId], queryFn: () => assistantService.listBrokerKeyCalls(keyId) });
  if (calls.isLoading) return <p className="text-xs text-gray-500">Loading calls…</p>;
  if (calls.isError) return <p role="alert" className="text-xs text-red-600">Could not load the calls.</p>;
  const rows = Array.isArray(calls.data) ? calls.data : [];
  if (rows.length === 0) return <p className="text-xs text-gray-500">No calls yet.</p>;
  return (
    <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
      {rows.map((call) => (
        <li key={call.id} className="flex gap-3">
          <span>{formatDateTime(call.created_at)}</span>
          <span className="font-mono">{call.tool}</span>
          <span>{call.status}{call.error_code ? ` (${call.error_code})` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

export function BrokerAssistantKeysSection() {
  const queryClient = useQueryClient();
  const keys = useQuery({ queryKey: ['assistant-broker-keys'], queryFn: () => assistantService.listBrokerKeys() });
  const [open, setOpen] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => assistantService.revokeBrokerKey(id, reason),
    onSuccess: () => {
      toast.success('Key revoked');
      queryClient.invalidateQueries({ queryKey: ['assistant-broker-keys'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const rows: AssistantKey[] = Array.isArray(keys.data) ? keys.data : [];

  return (
    <Card className="p-6 space-y-4">
      <CardHeader title="AI assistants" subtitle="Personal MCP keys held by your POSPs and staff. Every call is logged." />
      {keys.isLoading && <p className="text-sm text-gray-500">Loading keys…</p>}
      {keys.isError && <p role="alert" className="text-sm text-red-600">Could not load the keys. Try again.</p>}
      {keys.isSuccess && rows.length === 0 && <p className="text-sm text-gray-500">No assistant keys at this broker yet.</p>}
      {rows.length > 0 && (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((key) => (
            <li key={key.id} className="py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{key.name}</p>
                  <p className="text-xs text-gray-500">{`${key.owner_name} · ${key.owner_role}`}</p>
                  <p className="truncate font-mono text-xs text-gray-500">{key.display_key}</p>
                  {!key.is_active && <p className="text-xs text-gray-500">Revoked{key.revoke_reason ? `: ${key.revoke_reason}` : ''}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setOpen(open === key.id ? null : key.id)}>Recent calls</Button>
                  {key.is_active && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        const reason = window.prompt(`Revoke "${key.name}"? Give a reason (shown to the holder).`);
                        if (reason !== null) revoke.mutate({ id: key.id, reason });
                      }}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
              {open === key.id && <RecentCalls keyId={key.id} />}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
